// Universal Approval Rule Engine — Workflow Configuration Master
//
// Admin page to configure universal approval rules across all PayrollPro modules with:
//   • Module Selector & Registry (25+ Modules)
//   • Priority-based conflict resolution
//   • 12 Criteria Filters (Amount, Dates, Depts, Branches/Locations, Designations, Grades, Categories, Employment Types, Cost Centers, Business Units)
//   • 12 Dynamic Approver Types per level (RM, FM, HR/Payroll/Finance Managers, Dept/Branch/BU Heads, Admins, Directors, Named Users, Specific Roles)
//   • Unlimited Dynamic Approval Levels with re-ordering & amount editing permissions
//
// Route: /line-item-approval/workflows
// RBAC: tenant_admin, super_admin, payroll_admin, hr_admin

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import {
  Plus, Save, Trash2, X, ChevronDown, ChevronUp, Users, SlidersHorizontal, Layers, CheckCircle2, AlertCircle, Building2, MapPin, Briefcase, Award, FolderKanban
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_LEVEL = () => ({
  order: 1,
  name: '',
  level_type: 'reporting_manager',
  approver_user_ids: [],
  approver_role: '',
  edit_permitted: false,
  notes: '',
});

const EMPTY_RULE = () => ({
  company_id: '',
  module: 'monthly_inputs',
  input_type: 'monthly_inputs',
  name: '',
  priority: 100,
  active: true,
  amount_min: '',
  amount_max: '',
  effective_from: '',
  effective_to: '',
  department_ids: [],
  location_ids: [],
  designation_ids: [],
  grade_ids: [],
  employee_categories: [],
  employment_types: [],
  cost_center_ids: [],
  business_unit_ids: [],
  levels: [EMPTY_LEVEL()],
});

export default function LineItemApprovalWorkflows() {
  const [meta, setMeta] = useState({ modules: [], approver_types: [], input_types: [], input_type_labels: {} });
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE());
  const [saving, setSaving] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [activeTab, setActiveTab] = useState('criteria'); // 'criteria' | 'levels'

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API_URL}/line-item-approval/meta`, { withCredentials: true });
        setMeta(m.data || {});
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch (e) {
        toast.error('Failed to load metadata or companies');
      }
    })();
  }, []);

  const loadRules = useCallback(async () => {
    if (!selCompany) return;
    try {
      const q = new URLSearchParams({ company_id: selCompany });
      if (selectedModule) q.set('module', selectedModule);
      const { data } = await axios.get(`${API_URL}/line-item-approval/workflows?${q.toString()}`, { withCredentials: true });
      setRules(data || []);
    } catch {
      setRules([]);
    }
  }, [selectedModule, selCompany]);

  useEffect(() => {
    if (!selCompany) return;
    (async () => {
      try {
        const [deptRes, locRes, desigRes, gradeRes, ccRes, buRes, userRes] = await Promise.all([
          axios.get(`${API_URL}/masters/departments?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/locations?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/designations?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/grades?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/cost-centers?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/business-units?company_id=${selCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/users?limit=1000`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDepartments(deptRes.data || []);
        setLocations(locRes.data || []);
        setDesignations(desigRes.data || []);
        setGrades(gradeRes.data || []);
        setCostCenters(ccRes.data || []);
        setBusinessUnits(buRes.data || []);
        setUsers((userRes.data || []).filter((x) => x.is_active !== false));
      } catch {
        // quiet error fallback
      }
      await loadRules();
    })();
  }, [selCompany, loadRules]);

  useEffect(() => {
    loadRules();
  }, [selectedModule, loadRules]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_RULE(), company_id: selCompany, module: selectedModule || 'monthly_inputs' });
    setActiveTab('criteria');
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      company_id: r.company_id || selCompany,
      module: r.module || r.input_type || 'monthly_inputs',
      input_type: r.input_type || r.module || 'monthly_inputs',
      name: r.name || '',
      priority: r.priority ?? 100,
      active: r.active !== false,
      amount_min: r.amount_min ?? '',
      amount_max: r.amount_max ?? '',
      effective_from: r.effective_from || '',
      effective_to: r.effective_to || '',
      department_ids: r.department_ids || [],
      location_ids: r.location_ids || [],
      designation_ids: r.designation_ids || [],
      grade_ids: r.grade_ids || [],
      employee_categories: r.employee_categories || [],
      employment_types: r.employment_types || [],
      cost_center_ids: r.cost_center_ids || [],
      business_unit_ids: r.business_unit_ids || [],
      levels: (r.levels || []).map((l) => ({
        order: l.order || 1,
        name: l.name || '',
        level_type: l.level_type || 'named_users',
        approver_user_ids: l.approver_user_ids || [],
        approver_role: l.approver_role || '',
        edit_permitted: !!l.edit_permitted,
        notes: l.notes || '',
      })),
    });
    setActiveTab('criteria');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const addLevel = () => setForm((f) => ({
    ...f,
    levels: [...f.levels, { ...EMPTY_LEVEL(), order: (f.levels.length + 1) }],
  }));

  const removeLevel = (idx) => setForm((f) => ({
    ...f,
    levels: f.levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i + 1 })),
  }));

  const moveLevel = (idx, dir) => setForm((f) => {
    const arr = [...f.levels];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...f, levels: arr.map((l, i) => ({ ...l, order: i + 1 })) };
  });

  const updateLevel = (idx, patch) => setForm((f) => ({
    ...f,
    levels: f.levels.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const toggleApproverUser = (levelIdx, userId) => setForm((f) => ({
    ...f,
    levels: f.levels.map((l, i) => {
      if (i !== levelIdx) return l;
      const cur = l.approver_user_ids || [];
      return cur.includes(userId)
        ? { ...l, approver_user_ids: cur.filter((x) => x !== userId) }
        : { ...l, approver_user_ids: [...cur, userId] };
    }),
  }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Rule name is required');
    if (!form.company_id) return toast.error('Company is required');
    if (!form.levels.length) return toast.error('At least one approval level is required');
    for (const l of form.levels) {
      if (!l.name.trim()) return toast.error(`Level name required for Level ${l.order}`);
      if ((l.level_type || 'named_users') === 'named_users' && !(l.approver_user_ids || []).length) {
        return toast.error(`Level '${l.name}' (Specific Users) requires at least one user selected`);
      }
      if (l.level_type === 'specific_role' && !l.approver_role.trim()) {
        return toast.error(`Level '${l.name}' (Specific Role) requires a role code`);
      }
    }

    setSaving(true);
    const body = {
      company_id: form.company_id,
      module: form.module,
      input_type: form.module,
      name: form.name.trim(),
      priority: Number(form.priority) || 100,
      active: !!form.active,
      amount_min: form.amount_min === '' ? null : Number(form.amount_min),
      amount_max: form.amount_max === '' ? null : Number(form.amount_max),
      effective_from: form.effective_from || null,
      effective_to: form.effective_to || null,
      department_ids: form.department_ids || [],
      location_ids: form.location_ids || [],
      designation_ids: form.designation_ids || [],
      grade_ids: form.grade_ids || [],
      employee_categories: form.employee_categories || [],
      employment_types: form.employment_types || [],
      cost_center_ids: form.cost_center_ids || [],
      business_unit_ids: form.business_unit_ids || [],
      levels: form.levels,
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/line-item-approval/workflows/${editingId}`, body, { withCredentials: true });
        toast.success('Approval rule updated successfully');
      } else {
        await axios.post(`${API_URL}/line-item-approval/workflows`, body, { withCredentials: true });
        toast.success('Approval rule created successfully');
      }
      closeForm();
      await loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save approval rule');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (r) => {
    if (!window.confirm(`Deactivate approval rule "${r.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/line-item-approval/workflows/${r.id}`, { withCredentials: true });
      toast.success('Rule deactivated');
      await loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const moduleLabel = (mCode) => {
    const found = (meta.modules || []).find((m) => m.code === mCode);
    return found ? found.label : meta.input_type_labels[mCode] || mCode;
  };

  const approverTypeLabel = (aCode) => {
    const found = (meta.approver_types || []).find((a) => a.code === aCode);
    return found ? found.label : aCode;
  };

  const groupedRules = useMemo(() => {
    const map = {};
    for (const r of rules) {
      const k = r.module || r.input_type || 'monthly_inputs';
      if (!map[k]) map[k] = [];
      map[k].push(r);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    }
    return map;
  }, [rules]);

  return (
    <MainLayout>
      <div className="p-6 bg-slate-50 min-h-screen" data-testid="universal-workflow-page">
        {/* Header Banner */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Layers className="w-5 h-5" /></span>
              <h1 className="text-xl font-bold text-slate-900">Universal Approval Rules</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Configure dynamic multi-level approval workflows across all PayrollPro modules (Monthly Inputs, Joiners, Resignations, Claims, Revisions, etc.).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="universal-company-select"
              value={selCompany}
              onChange={(e) => setSelCompany(e.target.value)}
              className="border border-slate-300 rounded-lg px-3.5 py-2 text-sm bg-white font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              data-testid="universal-add-rule-btn"
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all"
            >
              <Plus className="w-4 h-4" /> New Approval Rule
            </button>
          </div>
        </div>

        {/* Module Selector Toolbar */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex items-center gap-4 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Module Selector:</label>
          <select
            data-testid="universal-module-filter"
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            className="border border-slate-300 rounded-lg px-3.5 py-2 text-sm bg-slate-50 text-slate-800 font-medium focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Approval Modules ({meta.modules?.length || 0})</option>
            {(meta.modules || []).map((m) => (
              <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400 ml-auto font-mono">
            {rules.length} Active / Configured Rules
          </span>
        </div>

        {/* Rules List Grouped by Module */}
        <div className="space-y-6">
          {Object.keys(groupedRules).length === 0 && (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
              <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-700">No approval rules configured</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                No active rules found for the selected module. Click <span className="font-semibold text-indigo-600">New Approval Rule</span> to set up a multi-level workflow.
              </p>
            </div>
          )}

          {Object.keys(groupedRules).sort().map((mCode) => (
            <div key={mCode} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="bg-slate-100/80 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
                <span className="font-bold text-slate-800 text-sm flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
                  {moduleLabel(mCode)}
                </span>
                <span className="text-xs bg-white text-slate-600 px-2.5 py-0.5 rounded-full font-medium border border-slate-200">
                  {groupedRules[mCode].length} Rules
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-5 py-2.5">Priority</th>
                      <th className="px-5 py-2.5">Rule Name</th>
                      <th className="px-5 py-2.5">Amount Filter</th>
                      <th className="px-5 py-2.5">Criteria Scope</th>
                      <th className="px-5 py-2.5">Approval Levels</th>
                      <th className="px-5 py-2.5">Status</th>
                      <th className="px-5 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {groupedRules[mCode].map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-5 py-3 font-mono font-bold text-slate-700 text-xs">P-{r.priority}</td>
                        <td className="px-5 py-3 font-semibold text-slate-900">{r.name}</td>
                        <td className="px-5 py-3 text-xs text-slate-600 font-mono">
                          {r.amount_min == null && r.amount_max == null ? 'Any Amount' : `₹${r.amount_min ?? 0} – ₹${r.amount_max ?? '∞'}`}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-600">
                          <div className="flex flex-wrap gap-1">
                            {(r.department_ids || []).length > 0 && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Depts: {r.department_ids.length}</span>}
                            {(r.location_ids || []).length > 0 && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Locations: {r.location_ids.length}</span>}
                            {(r.designation_ids || []).length > 0 && <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">Desigs: {r.designation_ids.length}</span>}
                            {(r.department_ids || []).length === 0 && (r.location_ids || []).length === 0 && (r.designation_ids || []).length === 0 && (
                              <span className="text-slate-400">All Employees</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(r.levels || []).map((l, i) => (
                              <span key={i} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-medium px-2 py-0.5 rounded-md">
                                <span className="text-[10px] text-slate-400 font-mono">L{l.order}:</span> {l.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.active !== false ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                            {r.active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => openEdit(r)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold mr-3 hover:underline"
                          >
                            Edit
                          </button>
                          {r.active !== false && (
                            <button
                              onClick={() => deactivate(r)}
                              className="text-xs text-rose-600 hover:text-rose-800 font-semibold hover:underline"
                            >
                              Deactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        {/* Create / Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="universal-rule-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {editingId ? 'Edit Approval Rule' : 'Configure Universal Approval Rule'}
                  </h2>
                  <p className="text-xs text-slate-500">Set matching criteria scope and multi-level approver chain.</p>
                </div>
                <button onClick={closeForm} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Tabs */}
              <div className="flex border-b border-slate-200 bg-white px-6">
                <button
                  onClick={() => setActiveTab('criteria')}
                  className={`py-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 ${activeTab === 'criteria' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  <SlidersHorizontal className="w-4 h-4" /> 1. Rule & Criteria Scope
                </button>
                <button
                  onClick={() => setActiveTab('levels')}
                  className={`py-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 ${activeTab === 'levels' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  <Layers className="w-4 h-4" /> 2. Approval Chain ({form.levels.length} Levels)
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {activeTab === 'criteria' && (
                  <div className="space-y-5">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Target Approval Module *</label>
                        <select
                          value={form.module}
                          onChange={(e) => setForm({ ...form, module: e.target.value, input_type: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500"
                        >
                          {(meta.modules || []).map((m) => (
                            <option key={m.code} value={m.code}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Rule Name *</label>
                        <input
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="e.g. Claims > ₹50K → Finance & Director"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Priority Order (lower wins)</label>
                        <input
                          type="number"
                          value={form.priority}
                          onChange={(e) => setForm({ ...form, priority: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-mono"
                        />
                      </div>
                    </div>

                    {/* Amount & Date Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Amount From (₹)</label>
                        <input
                          type="number"
                          value={form.amount_min}
                          onChange={(e) => setForm({ ...form, amount_min: e.target.value })}
                          placeholder="No Minimum"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Amount To (₹)</label>
                        <input
                          type="number"
                          value={form.amount_max}
                          onChange={(e) => setForm({ ...form, amount_max: e.target.value })}
                          placeholder="No Maximum"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Effective From</label>
                        <input
                          type="date"
                          value={form.effective_from}
                          onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Effective To</label>
                        <input
                          type="date"
                          value={form.effective_to}
                          onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    {/* Organizational Criteria */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" /> Departments (blank = All)
                        </label>
                        <div className="border border-slate-200 rounded-lg p-3 max-h-36 overflow-y-auto bg-slate-50 space-y-1">
                          {departments.length === 0 && <div className="text-xs text-slate-400">No departments found</div>}
                          {departments.map((d) => (
                            <label key={d.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(form.department_ids || []).includes(d.id)}
                                onChange={(e) => setForm({
                                  ...form,
                                  department_ids: e.target.checked
                                    ? [...(form.department_ids || []), d.id]
                                    : (form.department_ids || []).filter((x) => x !== d.id),
                                })}
                                className="rounded text-indigo-600 focus:ring-indigo-500"
                              />
                              {d.name}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" /> Branches / Locations (blank = All)
                        </label>
                        <div className="border border-slate-200 rounded-lg p-3 max-h-36 overflow-y-auto bg-slate-50 space-y-1">
                          {locations.length === 0 && <div className="text-xs text-slate-400">No locations found</div>}
                          {locations.map((loc) => (
                            <label key={loc.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(form.location_ids || []).includes(loc.id)}
                                onChange={(e) => setForm({
                                  ...form,
                                  location_ids: e.target.checked
                                    ? [...(form.location_ids || []), loc.id]
                                    : (form.location_ids || []).filter((x) => x !== loc.id),
                                })}
                                className="rounded text-indigo-600 focus:ring-indigo-500"
                              />
                              {loc.name || loc.branch_name}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'levels' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-slate-800">Dynamic Multi-Level Approver Chain</h3>
                        <p className="text-xs text-slate-500">Configure sequential approvers (Level 1 → Level 2 → Level 3 ...)</p>
                      </div>
                      <button
                        onClick={addLevel}
                        className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-lg border border-indigo-200 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add Level
                      </button>
                    </div>

                    <div className="space-y-3">
                      {form.levels.map((lvl, idx) => (
                        <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <span className="w-7 h-7 bg-indigo-600 text-white rounded-lg flex items-center justify-center text-xs font-mono font-bold shadow-sm">
                              L{lvl.order}
                            </span>
                            <input
                              value={lvl.name}
                              onChange={(e) => updateLevel(idx, { name: e.target.value })}
                              placeholder="Level Name (e.g. Reporting Manager, Finance Admin, CFO)"
                              className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium"
                            />
                            <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!lvl.edit_permitted}
                                onChange={(e) => updateLevel(idx, { edit_permitted: e.target.checked })}
                                className="rounded text-indigo-600"
                              />
                              Allow Amount Edits
                            </label>
                            <button onClick={() => moveLevel(idx, -1)} className="p-1 hover:bg-slate-200 rounded-md"><ChevronUp className="w-4 h-4 text-slate-600" /></button>
                            <button onClick={() => moveLevel(idx, 1)} className="p-1 hover:bg-slate-200 rounded-md"><ChevronDown className="w-4 h-4 text-slate-600" /></button>
                            {form.levels.length > 1 && (
                              <button onClick={() => removeLevel(idx)} className="p-1 hover:bg-rose-100 text-rose-600 rounded-md"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>

                          {/* Approver Type Selection */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                            <div>
                              <label className="block text-xs font-semibold text-slate-600 mb-1">Approver Type *</label>
                              <select
                                value={lvl.level_type}
                                onChange={(e) => updateLevel(idx, { level_type: e.target.value })}
                                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white font-semibold text-slate-800"
                              >
                                {(meta.approver_types || []).map((at) => (
                                  <option key={at.code} value={at.code}>{at.label}</option>
                                ))}
                              </select>
                            </div>

                            {lvl.level_type === 'specific_role' && (
                              <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Role Key *</label>
                                <input
                                  value={lvl.approver_role || ''}
                                  onChange={(e) => updateLevel(idx, { approver_role: e.target.value })}
                                  placeholder="e.g. finance_admin, hr_manager, cfo"
                                  className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white font-mono"
                                />
                              </div>
                            )}
                          </div>

                          {/* User Selector for Named Users */}
                          {lvl.level_type === 'named_users' && (
                            <div>
                              <div className="text-xs font-semibold text-slate-600 mb-1">
                                Specific Users ({(lvl.approver_user_ids || []).length} selected)
                              </div>
                              <div className="border border-slate-200 rounded-lg p-2.5 bg-white max-h-32 overflow-y-auto flex flex-wrap gap-2">
                                {users.map((u) => {
                                  const sel = (lvl.approver_user_ids || []).includes(u.id);
                                  return (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => toggleApproverUser(idx, u.id)}
                                      className={`text-xs px-2.5 py-1 rounded-md border font-medium transition-all ${sel ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                                    >
                                      {u.full_name || u.email}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                <button onClick={closeForm} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 rounded-lg">
                  Cancel
                </button>
                <div className="flex items-center gap-3">
                  {activeTab === 'criteria' ? (
                    <button
                      onClick={() => setActiveTab('levels')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 text-sm font-semibold rounded-lg shadow-sm"
                    >
                      Next: Configure Approval Chain →
                    </button>
                  ) : (
                    <button
                      disabled={saving}
                      onClick={submit}
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Approval Rule'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
